import type { AxiosPromise } from 'axios';
import { VIDEO_URL } from 'Constants/env';
import {
  COMMUNICATOR_VIDEO_SUMMARY,
  COMMUNICATOR_VIDEO_TRANSCRIPTION,
} from 'Constants/featureFlags';
import useFeatureFlags from 'Hooks/featureFlags/useFeatureFlags';
import { MobXProviderContext, observer } from 'mobx-react';
import { IVideoHistoryDetails } from 'Models/VideoHistoryModel';
import { useUIStore } from 'Modules/ui/index.store';
import moment from 'moment-timezone';
import * as React from 'react';
import { useLocation } from 'react-router';
import { Button } from 'semantic-ui-react';
import { RootStoreProps } from 'Stores/RootStore.types';
import { pushToGTMDataLayer } from 'Utils/analytics';
import { Attendees } from './Attendees';
import { ConnectionInfo } from './ConnectionInfo';
import { HistoryDetailsForbidden } from './HistoryDetailsForbidden';
import { Recordings } from './Recordings';
import { RecordingModal } from './RecordingsModal';
import { ShortHistory } from './ShortHistory';

const VideoHistoryDetails: React.FC = () => {
  const location = useLocation();

  const {
    ui: uiStore,
    person: personStore,
    conversation: conversationStore,
  } = React.useContext<RootStoreProps>(MobXProviderContext);

  const {
    setIsChatHistoryOpen,
    setVideoSummarySidePanelInfo,
    resetVideoSummarySidePanel,
  } = useUIStore();

  const {
    [COMMUNICATOR_VIDEO_TRANSCRIPTION]: isVideoTranscriptionEnabled,
    [COMMUNICATOR_VIDEO_SUMMARY]: isVideoSummaryEnabled,
  } = useFeatureFlags([
    COMMUNICATOR_VIDEO_TRANSCRIPTION,
    COMMUNICATOR_VIDEO_SUMMARY,
  ]);

  const [showModal, setShowModal] = React.useState(false);
  const [sharableLink, setSharableLink] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [exipiresIn, setExipiresIn] = React.useState('');
  const [deleted, setDeleted] = React.useState({
    personData: null,
    date: '',
    time: '',
  });

  React.useEffect(() => {
    conversationStore.setVideoHistoryDetails(null);
    loadHistoryDetails();
  }, []);

  const loadHistoryDetails = () => {
    const historyId = parseInt(location.pathname.split('/').pop());
    const resp: AxiosPromise<IVideoHistoryDetails> =
      conversationStore.loadDetailsVideoHistory(historyId);
    void resp.then((videoData) => {
      const recording = videoData.data.recordings[0];
      if (recording?.deletedByPersonId) {
        loadPersonData(recording.deletedByPersonId, recording.deletedAt);
      }
    });
  };

  const loadPersonData = (personId: number, date: string) => {
    const personResp = personStore.loadPersonByIdGetIfMissingGet(
      personId.toString()
    );
    personResp.case({
      fulfilled: (personPbo) => {
        const deletedDate = moment(date).format('dddd, MMMM DD. YYYY.');
        const time = moment(date).format('hh:mm A');
        setDeleted({
          personData: personPbo,
          date: deletedDate,
          time: time,
        });
      },
    });
  };

  const toggleRecordingsModal = () => setShowModal(!showModal);

  const handleOpenRecordingLink = (recordingId: string) => () => {
    const historyId = parseInt(location.pathname.split('/').pop());
    if (recordingId) {
      const respRecordings = conversationStore.loadVideoRecordings(
        historyId,
        recordingId,
        true
      );
      void respRecordings.then((recordings: any) => {
        const recording = recordings.data[0];
        const baseUrl: string = VIDEO_URL.slice(0, -1);
        setSharableLink(baseUrl + (recording.downloadLink as string));
        setPassword(recording.password);
        setExipiresIn(recording.exipiresIn);
        setShowModal(true);
      });
    }
  };

  return (
    <div className="history-details semantic-flex-row">
      <div className="page-title">Meeting Details</div>
      <div className="third-container button-chat-history flex gap-2 flex-wrap">
        {isVideoSummaryEnabled && isVideoTranscriptionEnabled && (
          <Button
            content="VIEW SUMMARY"
            onClick={() => {
              const conferenceId =
                conversationStore.videoHistoryDetails?.data?.id;
              if (!conferenceId) {
                return;
              }

              pushToGTMDataLayer('transcription_accessed_post_call', {
                conferenceId,
              });
              setIsChatHistoryOpen(false);
              setVideoSummarySidePanelInfo({
                conferenceId,
              });
            }}
            data-testid="video-history-details-view-summary-button"
          />
        )}
        <Button
          content="VIEW CHAT HISTORY"
          onClick={() => {
            resetVideoSummarySidePanel();
            setIsChatHistoryOpen(true);
          }}
        />
      </div>
      <div className="history-details-container semantic-flex-row">
        <ShortHistory
          videoHistoryDetails={conversationStore.videoHistoryDetails}
        />
        <Attendees conversation={conversationStore} ui={uiStore} />
      </div>
      <ConnectionInfo ui={uiStore} conversation={conversationStore} />
      <Recordings
        deleted={deleted}
        handleOpenRecordingLink={handleOpenRecordingLink}
        loadHistoryDetails={loadHistoryDetails}
        conversation={conversationStore}
        ui={uiStore}
        location={location}
      />
      {showModal && (
        <RecordingModal
          ui={uiStore}
          password={password}
          exipiresIn={exipiresIn}
          sharableLink={sharableLink}
          showModal={showModal}
          toogleRecordingsModal={toggleRecordingsModal}
          displayName={conversationStore.videoHistoryDetails?.data?.displayName}
        />
      )}
      {uiStore.displayForbiddenScreen && <HistoryDetailsForbidden />}
    </div>
  );
};

export default observer(VideoHistoryDetails);
